import { useEffect, useRef, useState } from "react";
import equal from "fast-deep-equal";
import { getInterventionComponent } from "../interventionComponentList";
import { ErrorBoundary } from "../ErrorBoundary";
import { updateInterventionStatus } from "../../utils";
import { getInterventionsAirbrake } from "../../utils/getInterventionsAirbrake";
import type { InterventionProps, InterventionEvent } from "../../types";

const airbrake = getInterventionsAirbrake();

export type IProps = {
  interventionName: string;
  componentName: string;
  site: string;
  vertical?: string;
  duid: string;
  uid: string;
};

const InterventionWithShownImpl = (props: IProps) => {
  const listenerRef = useRef<EventListener | null>(null);
  const [interventionProps, setInterventionProps] =
    useState<InterventionProps | null>(null);
  const onShow = useRef<(() => void) | undefined>(undefined);
  const [shown, setShown] = useState(false);

  const setOnShow = (data: InterventionEvent) => {
    onShow.current = data.onPopupShow;
    // delete onPopupShow so it doesn't trigger a diff in
    // InterventionProps and a rerender
    delete data.onPopupShow;
  };

  useEffect(() => {
    if (!listenerRef.current) {
      listenerRef.current = (event: Event) => {
        //@TODO: later, if possible, type this more safely
        const data = (event as CustomEvent).detail as InterventionEvent;

        if (data.interventionName !== props.interventionName) {
          return;
        }

        if (data.componentName !== props.componentName) {
          return;
        }

        if (!data.message) {
          return;
        }

        setOnShow(data);
        setInterventionProps((previousValue: InterventionProps | null) => {
          //Don't update state if it wouldn't change
          if (previousValue && equal(previousValue.data, data.message)) {
            return previousValue;
          }

          return {
            site: props.site,
            duid: props.duid,
            vertical: props.vertical,
            data: data.message,
            fireEvent: data.fireEvent,
            onDismiss: data.onPopupClose,
          };
        });
      };
    }

    window.addEventListener("intervention-event", listenerRef.current);

    return () => {
      if (listenerRef.current) {
        window.removeEventListener("intervention-event", listenerRef.current);
      }
    };
  }, [props, setInterventionProps]);

  //The first time that this intervention would be shown, fire the shown callback. If it succeeds, we won't fire shown again, even on re-renders.
  useEffect(() => {
    if (shown || !interventionProps) {
      return;
    }

    const informIDMOfShowingFn = async () => {
      const res = await updateInterventionStatus(
        props.duid,
        props.interventionName,
        props.site,
        props.vertical,
      );

      if (!res) {
        interventionProps?.fireEvent?.("componentNotShown");
        return;
      }

      if (onShow.current) {
        onShow.current();
      }
      interventionProps?.fireEvent?.("componentMounted");

      setShown(true);
    };

    void informIDMOfShowingFn();
  }, [interventionProps, props, shown, setShown]);

  if (!interventionProps || !shown) {
    return null;
  }

  const component = getInterventionComponent(
    interventionProps,
    props.componentName,
  );

  if (!component) {
    void airbrake.notify({
      error: new Error(
        "Component not found in interventionComponentList: " +
          props.componentName,
      ),
      params: {
        source: "mobius-interventions",
        component: "Intervention",
        userId: props.duid,
      },
    });

    return null;
  }

  return (
    <ErrorBoundary duid={props.duid} site={props.site}>
      {component}
    </ErrorBoundary>
  );
};

export const Intervention = (props: IProps) => {
  return <InterventionWithShownImpl {...props} />;
};
